import type { Session } from '@supabase/supabase-js';
import type {
  UserProfile,
  UserRole,
  V2AuthContext,
  V2AuthContextMembership,
} from '@shared/types/auth';

const isMembershipActive = (membership: V2AuthContextMembership) => membership.status === 'active';

export const deriveOrganizationRoles = (
  memberships: V2AuthContextMembership[],
  activeRoleTemplateCode?: string | null,
): Array<'owner' | 'doctor' | 'staff'> => {
  const roles = new Set<'owner' | 'doctor' | 'staff'>();

  const roleTemplateCodes = [
    ...memberships.filter(isMembershipActive).map((membership) => membership.roleTemplateCode),
    ...(activeRoleTemplateCode ? [activeRoleTemplateCode] : []),
  ];

  roleTemplateCodes.forEach((roleTemplateCode) => {
    if (roleTemplateCode.endsWith('.owner')) {
      roles.add('owner');
      return;
    }
    if (roleTemplateCode.endsWith('.doctor')) {
      roles.add('doctor');
      return;
    }
    if (roleTemplateCode.includes('staff')) {
      roles.add('staff');
    }
  });

  return Array.from(roles);
};

export const deriveUserRoleFromAuthContext = (context: V2AuthContext): UserRole => {
  switch (context.userType) {
    case 'patient':
      return 'patient';
    case 'doctor':
      return 'doctor';
    case 'super_admin':
      return 'super_admin';
    case 'staff':
    case 'admin':
      return 'admin';
    default:
      if (context.capabilities.some((capability) => capability.startsWith('iam.') || capability.startsWith('audit.'))) {
        return 'super_admin';
      }
      if (
        context.capabilities.some((capability) =>
          [
            'cases.manage',
            'logistics.manage',
            'patient_offers.publish',
            'billing.manage',
            'intake.review',
          ].includes(capability),
        )
      ) {
        return 'admin';
      }
      if (context.capabilities.some((capability) => capability.startsWith('doctor_quotes.'))) {
        return 'doctor';
      }
      return 'patient';
  }
};

export const buildUserProfileFromV2AuthContext = (
  session: Session,
  context: V2AuthContext,
): UserProfile => {
  const role = deriveUserRoleFromAuthContext(context);
  const organizationRoles = deriveOrganizationRoles(context.memberships, context.activeRoleTemplateCode);
  const firstName =
    context.profile.firstName ||
    (session.user.user_metadata?.first_name as string | undefined) ||
    'User';
  const lastName =
    context.profile.lastName ||
    (session.user.user_metadata?.last_name as string | undefined) ||
    'Name';

  return {
    id: context.authUserId || session.user.id,
    role,
    as_patient_id: context.asPatientId ?? (role === 'patient' ? context.userId : undefined),
    as_doctor_id: context.asDoctorId ?? (role === 'doctor' ? context.userId : undefined),
    email: session.user.email || context.primaryEmail || 'user@example.com',
    first_name: firstName,
    last_name: lastName,
    onboarding_complete: context.onboardingComplete ?? true,
    organization_roles: organizationRoles,
    profile_image: context.profileImage ?? undefined,
    user_type: context.userType,
    active_organization_id: context.activeOrganizationId,
    active_role_template_code: context.activeRoleTemplateCode,
    memberships: context.memberships,
    capabilities: context.capabilities,
  };
};

export const hasCapability = (
  capabilities: string[] | null | undefined,
  requiredCapability: string,
): boolean => (capabilities ?? []).includes(requiredCapability);

export const hasAnyCapability = (
  capabilities: string[] | null | undefined,
  requiredCapabilities: string[],
): boolean => requiredCapabilities.some((requiredCapability) => hasCapability(capabilities, requiredCapability));

export const hasAllCapabilities = (
  capabilities: string[] | null | undefined,
  requiredCapabilities: string[],
): boolean => requiredCapabilities.every((requiredCapability) => hasCapability(capabilities, requiredCapability));
